import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FiArrowUpRight, FiCheck, FiMail } from "react-icons/fi";
import siteConfig from "../config/siteConfig";
import "../css/contactus.scss";

const subjectOptions = [
  { value: "product", label: "Product question" },
  { value: "order", label: "Order support" },
  { value: "shipping", label: "Shipping & delivery" },
  { value: "return", label: "Return or exchange" },
  { value: "warranty", label: "Warranty claim" },
  { value: "other", label: "Something else" },
];

const emptyForm = (subject = "") => ({
  firstName: "",
  lastName: "",
  email: "",
  orderNumber: "",
  subject,
  message: "",
});

function Contact() {
  const [searchParams] = useSearchParams();
  const requestedSubject = searchParams.get("subject") || "";
  const presetSubject = subjectOptions.some(
    (option) => option.value === requestedSubject
  )
    ? requestedSubject
    : "";
  const [formData, setFormData] = useState(() => emptyForm(presetSubject));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [errors, setErrors] = useState({});
  const successRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!submission || !successRef.current) return;
    successRef.current.focus();
    successRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [submission]);

  const validateField = (name, value) => {
    const normalized = value.trim();

    if (name === "firstName" && !normalized) return "Enter your first name.";
    if (name === "lastName" && !normalized) return "Enter your last name.";
    if (name === "email") {
      if (!normalized) return "Enter your email address.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
        return "Enter a valid email address.";
      }
    }
    if (name === "subject" && !normalized) return "Choose a topic.";
    if (name === "message") {
      if (!normalized) return "Tell us how we can help.";
      if (normalized.length < 20) return "Please add a little more detail.";
    }

    return "";
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => {
      if (!previous[name]) return previous;
      const nextErrors = { ...previous };
      delete nextErrors[name];
      return nextErrors;
    });
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    const message = validateField(name, value);
    setErrors((previous) => {
      const nextErrors = { ...previous };
      if (message) nextErrors[name] = message;
      else delete nextErrors[name];
      return nextErrors;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const fieldsToValidate = [
      "firstName",
      "lastName",
      "email",
      "subject",
      "message",
    ];
    const formErrors = fieldsToValidate.reduce((result, field) => {
      const message = validateField(field, formData[field]);
      if (message) result[field] = message;
      return result;
    }, {});

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      const firstInvalidField = event.currentTarget.querySelector(
        `[name="${Object.keys(formErrors)[0]}"]`
      );
      firstInvalidField?.focus();
      return;
    }

    setIsSubmitting(true);
    setErrors({});
    await new Promise((resolve) => window.setTimeout(resolve, 900));

    if (!mountedRef.current) return;

    setSubmission({
      firstName: formData.firstName.trim(),
      id: `SUP-${Date.now().toString(36).slice(-6).toUpperCase()}`,
    });
    setFormData(emptyForm());
    setIsSubmitting(false);
  };

  const startAnotherMessage = () => {
    setSubmission(null);
    setErrors({});
  };

  return (
    <main className="contact">
      <header className="contact__hero">
        <div className="contact__hero-copy">
          <p className="contact__eyebrow">SUPPORT / {siteConfig.wordmark}</p>
          <h1>CONTACT<br />US</h1>
          <p>
            Product questions, order updates, or setup advice—send the details
            and our support team will point you in the right direction.
          </p>
        </div>
        <figure className="contact__hero-media">
          <div className="contact__hero-collage">
            <img src="/img/product-24.webp" alt="Translucent red artisan keycaps" />
            <img src="/img/product-23.webp" alt="A vivid green coiled keyboard cable" />
            <img src="/img/product-17.webp" alt="A black and orange performance mouse" />
          </div>
          <figcaption>GEAR FOR EVERY PART OF THE SETUP</figcaption>
        </figure>
      </header>

      <section className="contact__support">
        <aside className="contact__support-aside">
          <p className="contact__eyebrow">BEFORE YOU SEND</p>
          <h2>START WITH<br />THE DETAILS.</h2>
          <p>
            Include your order number when your question is about an existing
            purchase. We typically reply within two business days during support
            hours.
          </p>

          <dl className="contact__details">
            <div>
              <dt><FiMail aria-hidden="true" /> EMAIL</dt>
              <dd>
                <a href={`mailto:${siteConfig.contact.email}`}>
                  {siteConfig.contact.email}
                </a>
              </dd>
            </div>
            <div>
              <dt>HOURS</dt>
              <dd>{siteConfig.contact.hours}</dd>
            </div>
            <div>
              <dt>STUDIO</dt>
              <dd>{siteConfig.contact.address}</dd>
            </div>
          </dl>

          <div className="contact__quick-links">
            <Link to="/shipping">SHIPPING INFO <FiArrowUpRight /></Link>
            <Link to="/returns">RETURN POLICY <FiArrowUpRight /></Link>
          </div>
        </aside>

        <div className="contact__form-panel">
          {submission ? (
            <section
              className="contact__success"
              ref={successRef}
              tabIndex="-1"
              role="status"
              aria-live="polite"
            >
              <span className="contact__success-icon"><FiCheck /></span>
              <p className="contact__eyebrow">REQUEST {submission.id}</p>
              <h2>MESSAGE<br />RECEIVED.</h2>
              <p>
                Thanks, {submission.firstName}. A confirmation is ready and our
                support team will respond at the email address you provided.
              </p>
              <button
                type="button"
                className="site-button site-button--dark"
                onClick={startAnotherMessage}
              >
                SEND ANOTHER MESSAGE
              </button>
            </section>
          ) : (
            <form
              className="contact__form"
              onSubmit={handleSubmit}
              noValidate
              aria-busy={isSubmitting}
            >
              <div className="contact__form-heading">
                <div>
                  <p className="contact__eyebrow">SUPPORT REQUEST</p>
                  <h2>HOW CAN WE HELP?</h2>
                </div>
                <span>* REQUIRED</span>
              </div>

              <div className="contact__field-grid">
                <div className="contact__field">
                  <label htmlFor="firstName">FIRST NAME *</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    autoComplete="given-name"
                    maxLength="40"
                    aria-invalid={Boolean(errors.firstName)}
                    aria-describedby={errors.firstName ? "firstName-error" : undefined}
                  />
                  {errors.firstName && <span id="firstName-error" className="contact__error">{errors.firstName}</span>}
                </div>

                <div className="contact__field">
                  <label htmlFor="lastName">LAST NAME *</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    autoComplete="family-name"
                    maxLength="40"
                    aria-invalid={Boolean(errors.lastName)}
                    aria-describedby={errors.lastName ? "lastName-error" : undefined}
                  />
                  {errors.lastName && <span id="lastName-error" className="contact__error">{errors.lastName}</span>}
                </div>

                <div className="contact__field">
                  <label htmlFor="email">EMAIL *</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    autoComplete="email"
                    maxLength="120"
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {errors.email && <span id="email-error" className="contact__error">{errors.email}</span>}
                </div>

                <div className="contact__field">
                  <label htmlFor="orderNumber">ORDER NUMBER (OPTIONAL)</label>
                  <input
                    id="orderNumber"
                    name="orderNumber"
                    type="text"
                    value={formData.orderNumber}
                    onChange={handleInputChange}
                    autoComplete="off"
                    maxLength="40"
                    placeholder="EXAMPLE: 10482"
                  />
                </div>

                <div className="contact__field">
                  <label htmlFor="subject">TOPIC *</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    aria-invalid={Boolean(errors.subject)}
                    aria-describedby={errors.subject ? "subject-error" : undefined}
                  >
                    <option value="">SELECT A TOPIC</option>
                    {subjectOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.subject && <span id="subject-error" className="contact__error">{errors.subject}</span>}
                </div>
              </div>

              <div className="contact__field contact__field--message">
                <div className="contact__message-label">
                  <label htmlFor="message">MESSAGE *</label>
                  <span>{formData.message.length} / 1000</span>
                </div>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  rows="7"
                  maxLength="1000"
                  placeholder="TELL US WHAT HAPPENED AND WHAT YOU NEED."
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={errors.message ? "message-error" : undefined}
                />
                {errors.message && <span id="message-error" className="contact__error">{errors.message}</span>}
              </div>

              <div className="contact__submit-row">
                <p>
                  By sending this form, you agree to our <Link to="/privacy">Privacy Policy</Link>.
                </p>
                <button
                  type="submit"
                  className="site-button site-button--dark"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <><span className="contact__spinner" aria-hidden="true" /> SENDING</>
                  ) : (
                    <>SEND REQUEST <FiArrowUpRight aria-hidden="true" /></>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}

export default Contact;
